import tippy from "tippy.js";

export function initLazyImages(root = document) {
    // Hard requirement: IntersectionObserver support
    if (!("IntersectionObserver" in window)) {
        return;
    }

    const images = Array.from(
        root.querySelectorAll("img[data-lazy-src]"),
    ).filter((img) => !img.dataset.lazyBound);

    if (!images.length) {
        return;
    }

    const io = new IntersectionObserver(
        (entries, obs) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) {
                    continue;
                }

                const img = entry.target;
                const src = img.getAttribute("data-lazy-src");
                if (!src) {
                    obs.unobserve(img);
                    continue;
                }

                if (img.dataset.lazyLoading) {
                    continue;
                }
                img.dataset.lazyLoading = "1";

                const probe = new Image();
                probe.decoding = "async";
                probe.onload = () => {
                    if (document.body.contains(img)) {
                        img.src = src;
                        img.removeAttribute("data-lazy-src");
                    }
                    cleanup();
                };

                probe.onerror = () => {
                    img.src =
                        "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="; // pixel
                    img.dataset.lazyError = "1";
                    cleanup();
                };

                probe.src = src;

                function cleanup() {
                    delete img.dataset.lazyLoading;
                    obs.unobserve(img);
                    // break references for gc
                    probe.onload = probe.onerror = null;
                }
            }
        },
        {
            root: null,
            rootMargin: "200px 0px", // pre-load slightly before visible
            threshold: 0,
        },
    );

    for (const img of images) {
        img.dataset.lazyBound = "1";
        io.observe(img);
    }
}

export function initImagePreviews(root = document) {
    root.querySelectorAll("[data-preview]").forEach((el) => {
        if (el._tippy) {
            return;
        }

        const box = document.createElement("div");
        box.style.pointerEvents = "none";
        box.innerHTML = `<img src="" style="display:block;object-fit:contain;" />`;
        const img = box.querySelector("img");

        tippy(el, {
            content: box,
            allowHTML: true,
            appendTo: () => document.body,
            maxWidth: "none",
            trigger: "mouseenter focus",
            duration: 0,
            hideOnClick: false,
            placement: el.dataset.previewPlacement || "right",
            onShow() {
                img.src = el.getAttribute("data-preview");
            },
            onHidden() {
                img.src = "";
            },
        });
    });
}

function boot(root = document) {
    initLazyImages(root);
    initImagePreviews(root);
}

function hookLivewireEvents() {
    Livewire.hook("morphed", ({ el, component }) => {
        if (el.hasAttribute("data-preview-parent")) {
            boot(el);
        }
    });
}

document.addEventListener("DOMContentLoaded", () => boot());
document.addEventListener("livewire:initialized", () => hookLivewireEvents());
